<script setup>
import { computed, ref, watch } from 'vue';
import { MessageBubble } from '@dashhudson/dashing-ui';
import { useIdentityStore } from '@/stores/identity';
import { useInteractionMenu } from '@/app/community/composables/useInteractionMenu';
import { useDateUtils } from '@/composables/useDateUtils';
import { getUserInitials } from '@/utils/user';
import { useCommunityMultiBrandsStore } from '@/stores/community-multi-brands';
import MessageText from '@/app/community/components/MessageText.vue';
import AttachmentGroup from '@/app/community/components/AttachmentGroup.vue';
import {
  COMMUNITY_ASSIGNMENT_COMMENT_TEXT,
  COMMUNITY_COMMENT_RESOURCE_TYPES,
  COMMUNITY_INTERACTION_TYPES,
  UNSUPPORTED_MESSAGE_TEXT,
  FAILED_RULE_DELETE_MESSAGE,
  FAILED_RULE_HIDE_MESSAGE,
  FAILED_RULE_DELETE_TOOLTIP_MESSAGE,
  FAILED_RULE_HIDE_TOOLTIP_MESSAGE,
  INSTAGRAM_DM_SHARE_TYPES,
  INSTAGRAM_DM_MEDIA_SHARED_TYPES,
} from '@/app/community/constants';
import { RULE_ACTION_KEYS, RULE_ACTION_TEXT_MAP } from '@/app/community/constants/inboxAutomation';
import AssignedTicketGroup from '@/app/community/components/AssignedTicketGroup.vue';
import { getInteractionGenericType } from '@/app/community/utils/community-interactions';
import { decamelizeKeys } from 'humps';
import { useCommunityStore } from '@/stores/community';
import { decodeSpansFromText, getSpanText } from '@/utils/commenting';
import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';

const props = defineProps({
  interaction: { type: Object, required: true },
  message: { type: Object, required: true },
});

const emits = defineEmits(['mediaLoaded']);

const identityStore = useIdentityStore();
const multiBrandStore = useCommunityMultiBrandsStore();
const communityStore = useCommunityStore();
const { convertTimestampToUserTimezone } = useDateUtils();

const target = computed(() => ({ ...props.interaction, message: props.message }));
const { conversationMessageMenu, translatedText, translated, translating, internalMessageMenu } =
  useInteractionMenu(target);

const showStoryMedia = ref(false);
const shouldContainStoryMedia = ref(false);

const internalMessageType = computed(() => props.message.resourceType);
const menuItems = computed(() =>
  internalMessageType.value
    ? (internalMessageMenu.value?.menuItems ?? [])
    : (conversationMessageMenu.value?.menuItems ?? []),
);
const numMenuButtons = computed(() =>
  internalMessageType.value
    ? (internalMessageMenu.value?.numMenuButtons ?? [])
    : conversationMessageMenu.value?.numMenuButtons,
);
const userAvatar = computed(() => {
  if (!props.message.sentByBrand) {
    return undefined;
  }
  const userReply = props.message.brandUserReply;
  const accessibleBrandUsers =
    multiBrandStore.brandIdToAccessibleBrandUsersMap[props.interaction.brandId] ?? [];
  const brandUser = accessibleBrandUsers.find((u) => u.id === userReply?.userId);
  if (brandUser) {
    return brandUser.avatarUrl
      ? { image: brandUser.avatarUrl }
      : { label: getUserInitials(brandUser) };
  }
  return undefined;
});
const follower = computed(() => props.interaction.followerParticipant);
const brand = computed(() => {
  const { avatarUrl } = identityStore.identityBrandsById[props.interaction?.brandId] ?? {};
  return { image: avatarUrl };
});
const orientation = computed(() => {
  if (!internalMessageType.value) return props.message.sentByBrand ? 'reply' : 'receipt';
  return 'reply';
});
const avatar = computed(() => {
  if (props.message.avatar) {
    return props.message.avatar;
  }
  if (props.message.sentByBrand) {
    return userAvatar.value ? userAvatar.value : brand.value;
  }
  return { image: follower.value.profilePictureUrl };
});
const timestamp = computed(() => convertTimestampToUserTimezone(props.message.createdAt));
const timezone = computed(() => identityStore.identity.time_zone_name);
const variant = computed(
  () => (props.message.sendFailed ? 'error' : props.message?.variant) ?? 'default',
);
const errorTooltip = computed(() => {
  if (props.message.sendFailed) {
    return 'Message failed to send';
  }
  if (props.message.deleteFailed) {
    return FAILED_RULE_DELETE_TOOLTIP_MESSAGE;
  }
  if (props.message.hideFailed) {
    return FAILED_RULE_HIDE_TOOLTIP_MESSAGE;
  }
  return undefined;
});
const isMediaShared = computed(
  () =>
    props.interaction.type === COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION &&
    Object.values(INSTAGRAM_DM_SHARE_TYPES).includes(props.message.shares?.type),
);
const isUnsupported = computed(
  () => props.message.isDhUnsupported || props.message.isFbUnsupported,
);
const storyData = computed(() => {
  if (props.interaction.type === COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION) {
    return props.message.storyReply ?? props.message.storyMention;
  }
  return undefined;
});

const mediaSharedData = computed(() => {
  if (isMediaShared.value) {
    return {
      assetUrl: props.message.shares?.assetData?.url,
      assetType: props.message.shares?.assetData?.type,
    };
  }
  return undefined;
});

const attachmentData = computed(() => mediaSharedData.value ?? storyData.value);
const attachments = computed(() => {
  const attachmentTarget = props.message.attachment ?? props.message.attachments;
  if (attachmentTarget) {
    return Array.isArray(attachmentTarget) ? attachmentTarget : [attachmentTarget];
  }
  return undefined;
});
const referencedUsers = computed(() =>
  decamelizeKeys(communityStore.internalMessagesReferencedUsers ?? {}),
);
const storyTitle = computed(() => {
  if (
    props.interaction.type === COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION &&
    storyData.value
  ) {
    const followerName = follower.value.username;
    if (props.message.storyMention) {
      return props.message.sentByBrand
        ? `You mentioned ${followerName} in your story`
        : 'Mentioned you in their story';
    }
    if (props.message.storyReply) {
      return props.message.sentByBrand
        ? `You replied to ${followerName}'s story`
        : 'Replied to your story';
    }
  }
  return undefined;
});
const mediaSharedTitle = computed(() => {
  if (isMediaShared.value) {
    const mediaType = INSTAGRAM_DM_MEDIA_SHARED_TYPES[props.message.shares.type];
    return props.message.sentByBrand
      ? `You shared a ${mediaType} with ${follower.value.username}`
      : `Shared a ${mediaType} with you`;
  }
  return undefined;
});
const attachmentTitle = computed(() => mediaSharedTitle.value ?? storyTitle.value);
const messageText = computed(() => {
  if (isUnsupported.value) {
    return UNSUPPORTED_MESSAGE_TEXT;
  }
  if (props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_EXECUTED) {
    const action = props.message.action;

    const actionKey = Object.keys(action || {}).find(
      (key) => action[key] && RULE_ACTION_TEXT_MAP[key],
    );
    if (actionKey) {
      const actionText = RULE_ACTION_TEXT_MAP[actionKey];
      return actionKey === RULE_ACTION_KEYS.PRESET_REPLY_ID
        ? `Automation Rule Executed. ${actionText}.`
        : `Automation Rule Executed. Message ${actionText}.`;
    }
    return `Automation Rule Executed: ${props.message.text}`;
  }
  if (
    props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_DELETE_FAILED
  ) {
    return FAILED_RULE_DELETE_MESSAGE;
  }
  if (props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_HIDE_FAILED) {
    return FAILED_RULE_HIDE_MESSAGE;
  }
  if (
    [
      COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_ASSIGNMENT,
      COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_INTERNAL_NOTE,
    ].includes(props.message.resourceType)
  ) {
    const isAssignment =
      props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_ASSIGNMENT;
    const interactionType = getInteractionGenericType(props.interaction.type);
    const baseText = isAssignment
      ? `${interactionType} assigned to ${props.message.text}`
      : props.message.text;
    if (props.message.text.match(/@\[\d+\]/)) {
      const textSpans = decodeSpansFromText(baseText, referencedUsers.value);
      return textSpans.map((span) => getSpanText(span, isAssignment)).join('');
    }
    if (props.message.text === COMMUNITY_ASSIGNMENT_COMMENT_TEXT.UNASSIGNED) {
      return `${interactionType} unassigned`;
    }
  }
  return translated.value ? translatedText.value : props.message.text;
});
const messageTextClass = computed(() => {
  if (props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_EXECUTED)
    return 'dash-text-knockout';
  return isUnsupported.value ||
    props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_ASSIGNMENT
    ? 'dash-text-subtle'
    : 'dash-text-primary';
});

function isSquarish(width, height) {
  if (width && height) {
    // width and height are within ~20% of each other
    return Math.abs(width / height - 1) < 0.2;
  }
  return false;
}

function onMediaLoad(e) {
  if (attachmentData.value?.assetType === 'image') {
    const { naturalHeight, naturalWidth } = e?.target ?? {};
    shouldContainStoryMedia.value = isSquarish(naturalWidth, naturalHeight);
    showStoryMedia.value = true;
  } else if (attachmentData.value?.assetType === 'video') {
    const { videoHeight, videoWidth } = e?.target ?? {};
    shouldContainStoryMedia.value = isSquarish(videoWidth, videoHeight);
    showStoryMedia.value = true;
  }
}

watch(
  () => showStoryMedia.value,
  (to) => {
    if (to) {
      emits('mediaLoaded');
    }
  },
);
</script>

<template>
  <MessageBubble
    :orientation="orientation"
    :avatar="avatar"
    :seen="message.seen"
    :date="new Date(timestamp)"
    :timezone="timezone"
    :variant="variant"
    :error-tooltip="errorTooltip"
    :menu-items="menuItems"
    :num-menu-buttons="numMenuButtons"
    :menu-props="{ variant: 'default' }"
    :translating="translating"
  >
    <span class="dash-text-subtle">{{ attachmentTitle }}</span>
    <div v-if="attachmentData" class="story-media">
      <img
        v-if="attachmentData.assetType === 'image'"
        v-show="showStoryMedia"
        ref="storyMedia"
        :class="[
          'dash-rounded-sm dash-w-40 !h-[18rem]',
          shouldContainStoryMedia ? 'dash-bg-background-action !object-contain' : '!object-cover',
        ]"
        :src="attachmentData.assetUrl"
        @load="onMediaLoad"
      />
      <video
        v-if="attachmentData.assetType === 'video'"
        v-show="showStoryMedia"
        ref="storyMedia"
        :src="attachmentData.assetUrl"
        class="dash-rounded-sm dash-w-40"
        controls="controls"
        controlslist="nodownload"
        @loadeddata="onMediaLoad"
      />
    </div>
    <MessageText :text="messageText" :class="['fs-mask', messageTextClass]" />
    <template v-if="variant === 'default'">
      <AttachmentGroup
        :attachments="attachments"
        flexible-dimensions
        @attachments-loaded="showStoryMedia = true"
      />
      <AssignedTicketGroup :message-id="message.id || message.uuid" />
    </template>
  </MessageBubble>
</template>
